import React from 'react';
import Logo from './Logo';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Download, FileImage, Palette } from 'lucide-react';

const LogoShowcase = () => {
  const handleDownload = (filename, path) => {
    const link = document.createElement('a');
    link.href = path;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = () => {
    const downloads = [
      { filename: 'vantage-point-primary-logo.svg', path: '/logo-assets/primary-logo.svg' },
      { filename: 'vantage-point-white-logo.svg', path: '/logo-assets/white-logo.svg' },
      { filename: 'vantage-point-icon-only.svg', path: '/logo-assets/icon-only.svg' },
      { filename: 'vantage-point-icon-white.svg', path: '/logo-assets/icon-white.svg' },
      { filename: 'vantage-point-compact-logo.svg', path: '/logo-assets/compact-logo.svg' },
      { filename: 'vantage-point-oia-partnership-logo.svg', path: '/logo-assets/partnership-logo.svg' }
    ];
    
    downloads.forEach((item, index) => {
      setTimeout(() => {
        handleDownload(item.filename, item.path);
      }, index * 200);
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-light text-gray-900 mb-6">
            Vantage Points Hospitality
          </h1>
          <p className="text-xl text-gray-600 font-light mb-8">
            Brand Identity & Logo System
          </p>
          
          {/* Download All Button */}
          <div className="flex justify-center">
            <Button 
              onClick={downloadAll}
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4"
            >
              <Download className="mr-3 h-5 w-5" />
              Download Complete Logo Package
            </Button>
          </div>
        </div>

        {/* Primary Logo */}
        <Card className="mb-12 border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl font-light">Primary Logo</CardTitle>
          </CardHeader>
          <CardContent className="p-12 bg-white">
            <div className="flex flex-col items-center space-y-8">
              <Logo variant="default" size="lg" />
              <div className="text-center">
                <p className="text-gray-600 font-light mb-6">
                  Main brand logo for web headers, business cards, and primary applications
                </p>
                <div className="flex gap-3 justify-center">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDownload('vantage-point-primary-logo.svg', '/logo-assets/primary-logo.svg')}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    SVG
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logo Variations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* White Version */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-light">White Version</CardTitle>
            </CardHeader>
            <CardContent className="p-12 bg-gray-900">
              <div className="flex flex-col items-center space-y-4">
                <Logo variant="white" size="lg" />
                <p className="text-white/70 text-sm font-light text-center mb-4">
                  For dark backgrounds and premium applications
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-white text-white hover:bg-white hover:text-gray-900"
                  onClick={() => handleDownload('vantage-point-white-logo.svg', '/logo-assets/white-logo.svg')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  SVG
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Compact Version */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-xl font-light">Compact Version</CardTitle>
            </CardHeader>
            <CardContent className="p-12 bg-white">
              <div className="flex flex-col items-center space-y-4">
                <Logo variant="compact" size="md" />
                <p className="text-gray-600 text-sm font-light text-center mb-4">
                  For mobile headers and space-constrained layouts
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownload('vantage-point-compact-logo.svg', '/logo-assets/compact-logo.svg')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  SVG
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Icon Variations */}
        <Card className="mb-12 border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl font-light">Icon Variations</CardTitle>
          </CardHeader>
          <CardContent className="p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Logo variant="icon-only" size="xl" />
                </div>
                <p className="text-sm text-gray-600 font-light">Extra Large</p>
                <p className="text-xs text-gray-500 mb-3">App icons</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownload('vantage-point-icon-only.svg', '/logo-assets/icon-only.svg')}
                >
                  <Download className="mr-1 h-3 w-3" />
                  SVG
                </Button>
              </div>
              
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Logo variant="icon-only" size="lg" />
                </div>
                <p className="text-sm text-gray-600 font-light">Large</p>
                <p className="text-xs text-gray-500 mb-3">Social media</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownload('vantage-point-icon-only.svg', '/logo-assets/icon-only.svg')}
                >
                  <Download className="mr-1 h-3 w-3" />
                  SVG
                </Button>
              </div>
              
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Logo variant="icon-only" size="md" />
                </div>
                <p className="text-sm text-gray-600 font-light">Medium</p>
                <p className="text-xs text-gray-500 mb-3">Buttons</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownload('vantage-point-icon-only.svg', '/logo-assets/icon-only.svg')}
                >
                  <Download className="mr-1 h-3 w-3" />
                  SVG
                </Button>
              </div>
              
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Logo variant="icon-only" size="sm" />
                </div>
                <p className="text-sm text-gray-600 font-light">Small</p>
                <p className="text-xs text-gray-500 mb-3">Notifications</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownload('vantage-point-icon-only.svg', '/logo-assets/icon-only.svg')}
                >
                  <Download className="mr-1 h-3 w-3" />
                  SVG
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Partnership Logo */}
        <Card className="mb-12 border-0 shadow-lg bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-2xl font-light text-emerald-800">Partnership Logo</CardTitle>
          </CardHeader>
          <CardContent className="p-12">
            <div className="flex flex-col items-center space-y-8">
              <div className="bg-white p-8 rounded-lg shadow-sm">
                <svg width="400" height="120" viewBox="0 0 400 120">
                  <defs>
                    <linearGradient id="partnershipGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#1a1a1a" />
                      <stop offset="100%" stopColor="#4a4a4a" />
                    </linearGradient>
                  </defs>
                  
                  {/* Vantage Points Logo */}
                  <g>
                    <polygon points="25,25 40,40 25,55 10,40" fill="url(#partnershipGrad)" />
                    <polygon points="25,30 35,40 25,50 15,40" fill="rgba(255,255,255,0.1)" />
                    <line x1="5" y1="65" x2="45" y2="65" stroke="#1a1a1a" strokeWidth="1" strokeLinecap="round" />
                    <circle cx="2" cy="65" r="1" fill="#1a1a1a" />
                    <circle cx="48" cy="65" r="1" fill="#1a1a1a" />
                    <text x="65" y="45" fontFamily="Inter, sans-serif" fontSize="18" fontWeight="300" fill="#1a1a1a">Vantage Points</text>
                    <text x="65" y="60" fontFamily="Inter, sans-serif" fontSize="8" fontWeight="300" fill="#666666" letterSpacing="1px">HOSPITALITY</text>
                  </g>
                  
                  {/* Partnership Connector */}
                  <g>
                    <text x="220" y="50" fontFamily="Inter, sans-serif" fontSize="14" fontWeight="300" fill="#10b981" textAnchor="middle">Official Partner</text>
                    <line x1="175" y1="40" x2="265" y2="40" stroke="#10b981" strokeWidth="1" strokeDasharray="3,3" />
                  </g>
                  
                  {/* OIA Section */}
                  <g>
                    <image x="280" y="25" width="40" height="30" href="https://customer-assets.emergentagent.com/job_hostease-global/artifacts/n2vzlpaz_OIA.png" />
                    <text x="330" y="45" fontFamily="Inter, sans-serif" fontSize="16" fontWeight="500" fill="#1a1a1a">Green Ventures</text>
                    <text x="330" y="60" fontFamily="Inter, sans-serif" fontSize="10" fontWeight="300" fill="#666666">& Portfolio Companies</text>
                  </g>
                  
                  {/* Partnership Badge */}
                  <rect x="130" y="80" width="180" height="30" rx="15" fill="#10b981" opacity="0.1" stroke="#10b981" strokeWidth="1"/>
                  <text x="220" y="94" fontFamily="Inter, sans-serif" fontSize="9" fontWeight="400" fill="#10b981" textAnchor="middle">OFFICIAL HOSPITALITY PARTNER</text>
                  <text x="220" y="105" fontFamily="Inter, sans-serif" fontSize="8" fontWeight="300" fill="#10b981" textAnchor="middle">SINCE 2023</text>
                </svg>
              </div>
              <div className="text-center">
                <p className="text-emerald-700 font-light mb-4">
                  Official partnership co-branding for OIA Green Ventures collaborations
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white"
                  onClick={() => handleDownload('vantage-point-oia-partnership-logo.svg', '/logo-assets/partnership-logo.svg')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Partnership SVG
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Brand Guidelines */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl font-light">Brand Guidelines</CardTitle>
          </CardHeader>
          <CardContent className="p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Design Philosophy</h4>
                <ul className="space-y-3 text-gray-600 font-light">
                  <li>• Geometric diamond represents elevated perspective and vantage points</li>
                  <li>• Clean lines convey professionalism and luxury</li>
                  <li>• Subtle gradients add depth and premium feel</li>
                  <li>• Minimal color palette ensures versatility</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Usage Rules</h4>
                <ul className="space-y-3 text-gray-600 font-light">
                  <li>• Maintain minimum clear space around logo</li>
                  <li>• Use high contrast backgrounds for legibility</li>
                  <li>• Prefer SVG format for web applications</li>
                  <li>• Use white version on dark backgrounds only</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-12 p-8 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-4 text-center">Color Palette</h4>
              <div className="flex justify-center space-x-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-900 rounded-lg mb-3 shadow-sm"></div>
                  <p className="text-sm font-light text-gray-600">Primary</p>
                  <p className="text-xs text-gray-500">#1a1a1a</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-600 rounded-lg mb-3 shadow-sm"></div>
                  <p className="text-sm font-light text-gray-600">Secondary</p>
                  <p className="text-xs text-gray-500">#4a4a4a</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-white border border-gray-200 rounded-lg mb-3 shadow-sm"></div>
                  <p className="text-sm font-light text-gray-600">White</p>
                  <p className="text-xs text-gray-500">#ffffff</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Download Package Info */}
        <Card className="mt-12 border-0 shadow-lg bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-2xl font-light text-center text-emerald-800">
              <FileImage className="inline-block mr-3 h-6 w-6" />
              Logo Package Contents
            </CardTitle>
          </CardHeader>
          <CardContent className="p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-emerald-800 mb-4">Included Files</h4>
                <ul className="space-y-2 text-emerald-700 font-light">
                  <li>• Primary Logo (SVG) - Full color version</li>
                  <li>• White Logo (SVG) - For dark backgrounds</li>
                  <li>• Icon Only (SVG) - Mark without text</li>
                  <li>• Icon White (SVG) - White icon version</li>
                  <li>• Compact Logo (SVG) - Space-efficient version</li>
                  <li>• Partnership Logo (SVG) - OIA co-branding version</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-emerald-800 mb-4">File Specifications</h4>
                <ul className="space-y-2 text-emerald-700 font-light">
                  <li>• Format: Scalable Vector Graphics (SVG)</li>
                  <li>• Color Profile: sRGB</li>
                  <li>• Fonts: Inter (embedded as paths)</li>
                  <li>• Compatible: Web, Print, Mobile</li>
                  <li>• License: Full commercial usage rights</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <div className="bg-white p-6 rounded-lg border border-emerald-200">
                <Palette className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
                <p className="text-emerald-800 font-medium mb-4">
                  Complete Brand Asset Package
                </p>
                <p className="text-emerald-700 text-sm mb-6">
                  All logo variations in professional-grade SVG format, ready for immediate use across digital and print applications.
                </p>
                <Button 
                  onClick={downloadAll}
                  size="lg" 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Download Complete Package
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LogoShowcase;